import React, { ChangeEventHandler, MouseEventHandler, useEffect, useState, useRef, useCallback } from "react";
import { RouteLog } from "../common/ObjectTypes";
import LogsFilter from "../activityLogs/ActivityLogsFilter";
import { ArrowDown, ArrowRight, SortedDown, SortedUp } from "../common/ProjectIcons";
import Spinner from "../common/Spinner";
import { HoverTooltip } from "../common/HoverTooltip";
import { useTimezone } from "../../context/TimezoneContext";
import { getUtcOffsets } from "../common/getUtcOffsets";
import { useTranslation } from "react-i18next";
import NoResults from "../common/NoResults";
import { tableClasses, skeletonClasses } from "../common/tailwindClassnames";
import { getLogLevelIcon, getLogLevelLabel } from "../activityLogs/logLevelIcons";
import { RelativeTimeTooltip } from "../activityLogs/RelativeTimeTooltip";

type RouteLogProps = {
    logs: RouteLog[],
    reloadLogs: boolean,
    isAppendingLogs?: boolean,
    prevCursor: string | null,
    nextCursor: string | null,
    selectedTimezone: any,
    onSelectTimezone: Function,
    onNextPageClicked: Function,
    onPrevPageClicked: MouseEventHandler,
    requestSort: Function,
    pageLoaded: boolean,
    filters: any[],
    globalSearchStr: string,
    onChangeLogGlobalSearch: (value: string) => void,
    onChangeLogStatus: ChangeEventHandler,
    onChangeLogType: ChangeEventHandler,
    onChangeLogDate: Function,
    onResetLogFilters: Function,
    onSelectLog: Function,
    onRefreshLogs: Function,
    logsError?: boolean
}

const isComplexValue = (value: any) => value !== null && typeof value === 'object';

// Render a stored offset (e.g. "-06:00", "+05:30", "+00:00") as a compact label
// for the logs header: "UTC-6", "UTC+5:30", "UTC".
export const formatShortUtcOffset = (value: string): string => {
    if (!value) return "UTC";
    const sign = value.trim().startsWith("-") ? "-" : "+";
    const [h, m] = value.replace(/^[+-]/, "").split(":");
    const hours = parseInt(h, 10) || 0;
    const minutes = parseInt(m, 10) || 0;
    if (hours === 0 && minutes === 0) return "UTC";
    const minutesPart = minutes ? `:${String(minutes).padStart(2, "0")}` : "";
    return `UTC${sign}${hours}${minutesPart}`;
};

const RouteDetailsLog = ({
    logs,
    reloadLogs,
    isAppendingLogs = false,
    prevCursor,
    nextCursor,
    selectedTimezone,
    onSelectTimezone,
    onNextPageClicked,
    onPrevPageClicked,
    requestSort,
    pageLoaded,
    filters,
    globalSearchStr,
    onChangeLogGlobalSearch,
    onChangeLogStatus,
    onChangeLogType,
    onChangeLogDate,
    onResetLogFilters,
    onSelectLog,
    onRefreshLogs,
    logsError = false
}: RouteLogProps) => {
    const { t } = useTranslation();
    const { timezone } = useTimezone();
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [timezones, setTimezones] = useState<any[]>([]);
    const [sortDirection, setSortDirection] = useState<boolean>(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [logDetails, setLogDetails] = useState<RouteLog | null>(null);
    const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

    // The logs list flows in the normal page, so we watch the window scroll
    // position and load the next page as it nears the bottom of the document.
    // Memoized on its inputs (including nextCursor) so it always reads the
    // current cursor; the listener effect below re-binds when it changes.
    const handleScroll = useCallback(() => {
        // Bail while a page is loading or appending so a scroll event in the gap
        // before the request settles can't re-trigger the same fetch.
        if (reloadLogs || isAppendingLogs) return;

        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const clientHeight = window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;

        // Only trigger next page load when the page is actually scrollable (so the
        // programmatic open-scroll on a short page can't fire this), we're very
        // close to the bottom (within 20px), and there's more content to load.
        if (scrollHeight > clientHeight && scrollTop + clientHeight >= scrollHeight - 20 && nextCursor) {
            onNextPageClicked(); // Load next page
        }
    }, [reloadLogs, isAppendingLogs, onNextPageClicked, nextCursor]);

    // Debounced handler ref
    const scrollTimeoutRef = useRef<number | null>(null);
    const debouncedScrollHandler = useCallback(() => {
        if (scrollTimeoutRef.current) {
            window.clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = window.setTimeout(() => {
            handleScroll();
        }, 50);
    }, [handleScroll]);
    const formatDatetime = (datetime: string) => {
        const date = new Date(datetime);

        // Parse timezone offset
        const sign = timezone.value.startsWith("-") ? -1 : 1;
        const hours = parseInt(timezone.value.slice(1, 3), 10);
        const minutes = parseInt(timezone.value.slice(3, 5), 10) || 0;

        const offsetMilliseconds = sign * (hours * 60 + minutes) * 60 * 1000;

        const adjustedDate = new Date(date.getTime() + offsetMilliseconds);

        const year = adjustedDate.getUTCFullYear();
        const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
        const day = String(adjustedDate.getUTCDate()).padStart(2, "0");
        const hoursFormatted = String(adjustedDate.getUTCHours()).padStart(2, "0");
        const minutesFormatted = String(adjustedDate.getUTCMinutes()).padStart(2, "0");
        const secondsFormatted = String(adjustedDate.getUTCSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
    };
    const toggleDetails = async (id: string) => {
        if (expandedRow === id) {
            setExpandedRow(null)
        } else {
            setExpandedRow(id)
            setCollapsedKeys(new Set());
            setLoadingDetails(true);
            let temp = await onSelectLog(id);
            setLogDetails(temp);
            setLoadingDetails(false);

        }
    }

    useEffect(() => {
        let temp = getUtcOffsets();
        setTimezones(temp);
    }, [])

    // When the logs finish loading, scroll the page down once so the Route
    // header/tabs move up and out of the way, pinning the filter just below the
    // fixed nav. We measure the filter's live position rather than using a fixed
    // offset so long Route names (which wrap the header onto extra lines)
    // still scroll the right amount. The nav height is read at runtime so it stays
    // correct whether or not the environment banner is present.
    const filterRef = useRef<HTMLDivElement | null>(null);
    const didAutoScroll = useRef(false);
    useEffect(() => {
        if (didAutoScroll.current || !pageLoaded || reloadLogs || logs.length === 0 || !filterRef.current) return;
        didAutoScroll.current = true;
        // Two nested rAFs: the first fires before the browser paints the loaded
        // table (layout still stale), the second after paint so
        // getBoundingClientRect() reflects the real layout.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!filterRef.current) return;
                const nav = document.querySelector("[data-testid='main-nav-wrapper']");
                const navHeight = nav ? nav.getBoundingClientRect().height : 0;
                const top = filterRef.current.getBoundingClientRect().top + window.scrollY - navHeight;
                window.scrollTo({ top, behavior: "smooth" });
            });
        });
    }, [pageLoaded, reloadLogs, logs.length])

    // A full (non-append) reload — filter, sort, refresh — can shrink the result
    // set. Since the list flows in the page, a stale scroll offset would otherwise
    // leave the filter/rows mispositioned. Re-arm the one-shot pin so it re-runs
    // once the new results render, bringing the filter back under the nav.
    // Appends (isAppendingLogs) keep the user's place and must not re-pin.
    useEffect(() => {
        if (reloadLogs && !isAppendingLogs) {
            didAutoScroll.current = false;
        }
    }, [reloadLogs, isAppendingLogs])

    useEffect(() => {
        window.addEventListener("scroll", debouncedScrollHandler, { passive: true });
        return () => {
            window.removeEventListener("scroll", debouncedScrollHandler);
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [debouncedScrollHandler]);

    const formatLogDetails = (logDetails: RouteLog | null, logLevel: number) => {
        if (!logDetails) return null;

        const isError = logLevel === 40;

        const isLongString = (value: any) =>
            typeof value === 'string' && (value.includes('\n') || value.length > 80);

        const formatValue = (key: string, value: any): React.ReactNode => {
            if (value === null || value === undefined) return <span className="text-neutral-gray-400">null</span>;
            if (typeof value === 'boolean') return <span>{String(value)}</span>;
            if (typeof value === 'string' || typeof value === 'number') {
                if (isLongString(value)) {
                    const collapsed = collapsedKeys.has(key);
                    if (collapsed) return <span className="text-neutral-gray-400">"…"</span>;
                    return <pre className="whitespace-pre-wrap font-mono text-xs overflow-x-auto">{value}</pre>;
                }
                return <span>{typeof value === 'string' ? `"${value}"` : String(value)}</span>;
            }
            const collapsed = collapsedKeys.has(key);
            if (collapsed) {
                return <span className="text-neutral-gray-400">{Array.isArray(value) ? '[…]' : '{…}'}</span>;
            }
            return (
                <pre className="whitespace-pre-wrap font-mono text-xs overflow-x-auto inline-block align-top">
                    {JSON.stringify(value, null, 2)}
                </pre>
            );
        };

        const renderRows = (entries: [string, any][], section: string) =>
            entries.map(([key, value]) => {
                const scopedKey = `${section}:${key}`;
                const isErrorField = isError && (
                    key === 'error' || key.includes('error') ||
                    key.includes('server_response_body') || key.includes('server_response_status') ||
                    (typeof value === 'string' && value.toLowerCase().includes('error'))
                );
                const hasToggle = isComplexValue(value) || isLongString(value);
                const collapsed = collapsedKeys.has(scopedKey);
                const toggle = () => setCollapsedKeys(prev => {
                    const next = new Set(prev);
                    collapsed ? next.delete(scopedKey) : next.add(scopedKey);
                    return next;
                });

                return (
                    <div key={key} className={`flex items-start gap-1.5 mb-1 ${isErrorField ? 'text-logs-orange' : ''} transition-colors duration-300`}>
                        {hasToggle
                            ? <button
                                type="button"
                                onClick={toggle}
                                className="shrink-0 text-secondary-gray hover:text-off-black transition-colors duration-150 cursor-pointer leading-none mt-px"
                                aria-label={collapsed ? t('Expand') : t('Collapse')}
                              >
                                  {collapsed ? '▶' : '▼'}
                              </button>
                            : <span className="shrink-0 w-[0.75rem]" />
                        }
                        <span className="shrink-0 whitespace-nowrap text-off-black">{key}:</span>
                        <span className={`flex-1 min-w-0 ${isErrorField ? '' : 'text-bright-blue'}`}>
                            {formatValue(scopedKey, value)}
                        </span>
                    </div>
                );
            });

        const topLevel = Object.entries(logDetails).filter(([k]) => k !== 'details');
        const details = logDetails.details ? Object.entries(logDetails.details) : [];

        return (
            <div className="w-full font-mono text-xs text-secondary-gray">
                {renderRows(topLevel, 'top')}
                {details.length > 0 && (
                    <>
                        <div className="mb-1">
                            <span className="w-40 inline-block text-off-black">details:</span>
                        </div>
                        <div className="pl-2">
                            {renderRows(details, 'details')}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div>
            <div ref={filterRef} className="flex flex-col w-full mt-4">

                <LogsFilter
                    globalSearchStr={globalSearchStr}
                    timezones={timezones}
                    selectedTimezone={selectedTimezone}
                    onChangeGlobalSearch={onChangeLogGlobalSearch}
                    onResetFilters={onResetLogFilters}
                    onChangeStatusFilter={onChangeLogStatus}
                    onChangeLogType={onChangeLogType}
                    onChangeLogDate={onChangeLogDate}
                    onSelectTimezone={onSelectTimezone}
                    onRefresh={onRefreshLogs}
                    reloadLogs={reloadLogs}
                />
                <div className="w-full h-auto mb-4">
                    <div
                        className={`flex flex-col w-full h-fit ${tableClasses.container}`}>
                        <table className="table-fixed w-full cursor-default">
                            <thead>
                                <tr className={tableClasses.header}>
                                    <th className={`${tableClasses.headerCell} pl-4 w-20`}>{t("Level")}</th>
                                    <th className={`py-3 px-1.5 w-40`}>
                                        <span onClick={() => {
                                            requestSort(sortDirection);
                                            setSortDirection(!sortDirection);
                                        }} className={tableClasses.sortIcon}>
                                            {t("Time")} ({formatShortUtcOffset(timezone?.value)}) {sortDirection ? <SortedUp /> : <SortedDown />}
                                        </span>
                                    </th>
                                    <th className={`py-3 pl-1 pr-1.5`}>{t("Title")}</th>
                                </tr>
                            </thead>
                            {reloadLogs && !isAppendingLogs && <tbody className="text-sm w-full">
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <tr key={i} className={tableClasses.row}>
                                        <td className={`${tableClasses.cell} pl-4 w-20`}>
                                            <div className="flex items-center gap-2">
                                                <div className={`h-3 w-3 ${skeletonClasses.base}`} />
                                                <div className={`h-4 w-4 ${skeletonClasses.base} rounded-full`} />
                                            </div>
                                        </td>
                                        <td className={`py-3 px-1.5 w-40`}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                        <td className={`py-3 pl-1 pr-1.5`}><div className={`h-4 ${i % 3 === 0 ? 'w-3/4' : i % 3 === 1 ? 'w-1/2' : 'w-2/3'} ${skeletonClasses.base}`} /></td>
                                    </tr>
                                ))}
                            </tbody>}
                            {logs.length === 0 && !reloadLogs && <tbody className="text-sm w-full">
                                <tr>
                                    <td colSpan={3} className="text-center py-8">
                                        {logsError ?
                                            <NoResults
                                                headerMsg={t("Failed to Load Logs" as any)}
                                                labelMsg={t("Unable to load activity logs. Please try again later." as any)}
                                            />
                                            :
                                            <NoResults
                                                headerMsg={t("No Results Found")}
                                                labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                            />}
                                    </td>
                                </tr>
                            </tbody>}
                            {logs.length >= 1 && (!reloadLogs || isAppendingLogs) && <tbody className="text-sm w-full">
                                {logs.map((log: RouteLog) => (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            className={`${tableClasses.row} border-t cursor-pointer`}
                                            onClick={() => toggleDetails(log.id)}
                                        >
                                            <td className={`${tableClasses.cell} pl-4 w-20 align-middle`}>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-dark-green transition-colors duration-300 cursor-pointer leading-none flex items-center">
                                                        {expandedRow === log.id ? <ArrowDown /> : <ArrowRight />}
                                                    </span>
                                                    <HoverTooltip
                                                        content={t(getLogLevelLabel(log.log_level) as any)}
                                                        ariaLabel={t(getLogLevelLabel(log.log_level) as any)}
                                                        role="img"
                                                        className="items-center cursor-default"
                                                    >
                                                        {getLogLevelIcon(log.log_level)}
                                                    </HoverTooltip>
                                                </div>
                                            </td>
                                            <td className={`py-3 px-1.5 text-left text-secondary-gray transition-colors duration-300 align-middle`}>
                                                <RelativeTimeTooltip isoDatetime={log.created_at}>
                                                    {formatDatetime(log.created_at)}
                                                </RelativeTimeTooltip>
                                            </td>
                                            <td className={`py-3 pl-1 pr-8 text-left truncate max-w-0 align-middle`}>
                                                {log.title}
                                            </td>
                                        </tr>
                                        {expandedRow === log.id && (
                                            <tr>
                                                <td colSpan={3} className={`${tableClasses.cell} pl-8 pr-8 pb-8 text-secondary-gray text-xs text-wrap bg-secondary-background`}>
                                                    {loadingDetails ? <Spinner /> : formatLogDetails(logDetails, log.log_level)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {isAppendingLogs && (
                                    <tr>
                                        <td colSpan={3} className="py-3 pl-4 text-left text-secondary-gray transition-colors duration-300">
                                            {t("Loading...")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default RouteDetailsLog;